import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов субсидий
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation,
  farmTypes
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчетов субсидий для сравнения.
        </p>
      </div>
    );
  }

  // Вспомогательная функция для получения названия типа хозяйства
  const getFarmTypeName = (typeId) => {
    const farmType = farmTypes?.find(type => type.id === typeId);
    return farmType ? farmType.name : 'Неизвестный тип';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'вариант' : savedCalculations.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calculation) => (
          <div 
            key={calculation.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calculation.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calculation.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calculation.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Размер хозяйства:</span>
                <span className="font-medium">
                  {calculation.formData.farmSize} га
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип хозяйства:</span>
                <span>
                  {getFarmTypeName(calculation.formData.farmType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Программы:</span>
                <span>
                  {[
                    calculation.formData.includeCapitalGrants ? 'Гранты' : null,
                    calculation.formData.includeOperationalSubsidies ? 'Операционные' : null,
                    calculation.formData.includeLoanSubsidies ? 'Кредиты' : null,
                    calculation.formData.includeExportSupport ? 'Экспорт' : null
                  ].filter(Boolean).join(', ') || 'Нет'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Общая сумма:</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(calculation.results.totalSubsidyAmount, 0)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComparisonSidebar;